import { z } from 'zod';
import { IdSchema } from './encoding.js';
/**
 * The Release 1 financial reports (CLR-13; FRD-FIN-038, FIN-053, FIN-129 to FIN-134, FIN-137):
 * figures read from posted journal lines and the sub-ledgers, never stored twice (AD-6). Money is a
 * decimal string (SDD §9.2). Every figure drills down to the transactions behind it.
 */
/** The dimension combination a report is narrowed to (FRD-FIN-053, FIN-129). */
export const ReportDimensionsSchema = z.object({
    costCenterId: IdSchema.optional(),
    projectId: IdSchema.optional(),
    clientId: IdSchema.optional(),
    supplierId: IdSchema.optional(),
    employeeId: IdSchema.optional(),
    assetId: IdSchema.optional(),
    locationId: IdSchema.optional(),
});
const Amounts = {
    openingDebit: z.string(),
    openingCredit: z.string(),
    periodDebit: z.string(),
    periodCredit: z.string(),
    closingDebit: z.string(),
    closingCredit: z.string(),
};
/** One trial balance row: an account (L5) or a group rolled up to its level (FRD-FIN-004). */
export const TrialBalanceRowSchema = z.object({
    level: z.string(),
    code: z.string(),
    name: z.string(),
    /** The account, for an L5 row; null for a rolled-up group. */
    accountId: IdSchema.nullable(),
    groupId: IdSchema.nullable(),
    path: z.string(),
    financialStatement: z.string().nullable(),
    normalBalance: z.string().nullable(),
    ...Amounts,
});
export const TrialBalanceSchema = z.object({
    from: z.string(),
    to: z.string(),
    level: z.string(),
    rows: z.array(TrialBalanceRowSchema),
    totals: z.object(Amounts),
    /** Whether total debits equal total credits (FRD-FIN-034): always true for a sound ledger. */
    balanced: z.boolean(),
});
/** One line of an account ledger (FRD-FIN-053): a posted line with its running balance. */
export const AccountLedgerLineSchema = z.object({
    journalId: IdSchema,
    documentNo: z.string(),
    postingDate: z.string(),
    lineNo: z.number().int(),
    sourceType: z.string(),
    journalKind: z.string(),
    sourceRecordType: z.string().nullable(),
    sourceRecordId: IdSchema.nullable(),
    reference: z.string().nullable(),
    description: z.string().nullable(),
    debit: z.string(),
    credit: z.string(),
    /** Debit minus credit so far, from the opening balance. */
    runningBalance: z.string(),
    costCenterId: IdSchema.nullable(),
    projectId: IdSchema.nullable(),
    clientId: IdSchema.nullable(),
    supplierId: IdSchema.nullable(),
    employeeId: IdSchema.nullable(),
    assetId: IdSchema.nullable(),
    locationId: IdSchema.nullable(),
});
export const AccountLedgerSchema = z.object({
    account: z.object({
        id: IdSchema,
        code: z.string(),
        name: z.string(),
        path: z.string(),
        normalBalance: z.string(),
    }),
    from: z.string(),
    to: z.string(),
    /** Debit minus credit before `from`. */
    openingBalance: z.string(),
    totalDebit: z.string(),
    totalCredit: z.string(),
    closingBalance: z.string(),
    lines: z.array(AccountLedgerLineSchema),
    /** True when the range holds more lines than one report shows; narrow the dates. */
    truncated: z.boolean(),
});
/** An account in a statement, on its own normal-balance side (FRD-FIN-013). */
export const StatementAccountSchema = z.object({
    accountId: IdSchema,
    code: z.string(),
    name: z.string(),
    amount: z.string(),
    /** A contra account is presented as a deduction from its group (FRD-FIN-014). */
    contra: z.boolean(),
});
export const StatementGroupSchema = z.lazy(() => z.object({
    groupId: IdSchema,
    level: z.string(),
    code: z.string(),
    name: z.string(),
    amount: z.string(),
    groups: z.array(StatementGroupSchema),
    accounts: z.array(StatementAccountSchema),
}));
/** Profit & Loss for a date range and dimension combination (FRD-FIN-129). */
export const ProfitAndLossSchema = z.object({
    from: z.string(),
    to: z.string(),
    /** The L1 classes of the P&L accounts, each with its hierarchy (FRD-FIN-004). */
    classes: z.array(StatementGroupSchema),
    /** Income less expenses: every P&L credit less every P&L debit in the range. */
    netProfit: z.string(),
});
/** Balance Sheet as at a date (FRD-FIN-130). */
export const BalanceSheetSchema = z.object({
    asAt: z.string(),
    classes: z.array(StatementGroupSchema),
    /**
     * The cumulative P&L result not yet closed into equity: year-end close is a later capability
     * (FRD-FIN-114), so the result is shown here rather than posted by the report.
     */
    unclosedProfit: z.string(),
    totalDebitSide: z.string(),
    totalCreditSide: z.string(),
    balanced: z.boolean(),
});
/** A refused posting attempt (FRD-FIN-038, FIN-036). */
export const PostingRejectionSchema = z.object({
    id: IdSchema,
    attemptedAt: z.string(),
    attemptedBy: IdSchema.nullable(),
    attemptedByProcess: z.string().nullable(),
    sourceRecordType: z.string().nullable(),
    sourceRecordId: IdSchema.nullable(),
    sourceDisplayName: z.string().nullable(),
    intendedPostingDate: z.string(),
    failures: z.array(z.object({
        lineNo: z.number().int(),
        accountRef: z.string().nullable(),
        rule: z.string(),
        message: z.string(),
    })),
});
const QueryDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'must be YYYY-MM-DD');
const QueryFlag = z.enum(['true', 'false']).optional();
/**
 * The query every ledger report takes: its dates, the dimension combination and, for the trial
 * balance, the hierarchy level to roll up to (FRD-FIN-004, FIN-053, FIN-129, FIN-130).
 */
export const LedgerReportQuerySchema = z.strictObject({
    from: QueryDate.optional(),
    to: QueryDate.optional(),
    asAt: QueryDate.optional(),
    accountId: IdSchema.optional(),
    level: z.enum(['L1', 'L2', 'L3', 'L4', 'L5']).optional(),
    includeZero: QueryFlag,
    /** The analysis report (FRD-FIN-133, FIN-134): expense or revenue, grouped by up to three keys. */
    kind: z.enum(['expense', 'revenue']).optional(),
    groupBy: z
        .string()
        .regex(/^(account|group|costCenter|employee|project|client|supplier|asset|month|capexOpex|directIndirect)(,(account|group|costCenter|employee|project|client|supplier|asset|month|capexOpex|directIndirect)){0,2}$/)
        .optional(),
    costCenterId: IdSchema.optional(),
    projectId: IdSchema.optional(),
    clientId: IdSchema.optional(),
    supplierId: IdSchema.optional(),
    employeeId: IdSchema.optional(),
    assetId: IdSchema.optional(),
    locationId: IdSchema.optional(),
});
/** The expense or revenue analysis (FRD-FIN-030, FIN-031, FIN-133, FIN-134). */
export const AnalysisReportSchema = z.object({
    from: z.string(),
    to: z.string(),
    kind: z.enum(['expense', 'revenue']),
    groupBy: z.array(z.string()),
    rows: z.array(z.object({
        keys: z.record(z.string(), z.object({ value: z.string().nullable(), label: z.string().nullable() })),
        debit: z.string(),
        credit: z.string(),
        amount: z.string(),
    })),
    total: z.string(),
});
//# sourceMappingURL=financial-reports.js.map